<template>
  <LoadingSpinner
    v-if="loading"
    :status="$t('envelope.loadingEnvelope')"
  />

  <PasswordEntryScreen
    v-else-if="passwordNeeded"
    :passwordStatus="passwordStatus"
    :callback="handleAuthorisation"
    :branding="branding"
  />

  <OtpScreen
    v-else-if="otpNeeded"
    :callback="handleAuthorisation"
    :branding="branding"
  />

  <div
    v-else
    class="container bg-lightGray"
  >
    <AppHeader :title="title" />
    <SideBarInfo />
    <Document />
    <DocumentFooter :isPreview="isPreview" />
    <Help :isPreview="isPreview" />
    <RejectDocument :isPreview="isPreview" />
    <WidgetVerify />
  </div>
</template>

<script setup>
import { ref, onBeforeMount, watch, computed, getCurrentInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useGetters, useActions } from 'vuex-composition-helpers/dist'

import { useFlagEnabled } from '@/composables/useFlagEnabled'

import AppHeader from '@/components/Header'
import SideBarInfo from '@/components/SideBarInfo'
import Help from '@/components/Help'
import RejectDocument from '@/components/RejectDocument'
import WidgetVerify from '@/components/WidgetVerify'
import Document from '@/components/Document'
import DocumentFooter from '@/components/Footer'
import LoadingSpinner from '@/components/LoadingSpinner'
import OtpScreen from '@/components/screens/OtpScreen.vue'
import PasswordEntryScreen from '@/components/screens/PasswordEntryScreen.vue'

const { proxy } = getCurrentInstance()

const route = useRoute()
const router = useRouter()

const {
  title,
  branding,
  widgetRedirectUrl,
  passwordStatus,
  passwordNeeded,
  otpNeeded,
  documentIndex,
  activeFieldId,
  hasEnvelope
} = useGetters({
  title: 'title',
  branding: 'branding',
  widgetRedirectUrl: 'widgetRedirectUrl',
  passwordStatus: 'auth/passwordStatus',
  passwordNeeded: 'auth/passwordNeeded',
  otpNeeded: 'auth/otpNeeded',
  documentIndex: 'documentIndex',
  activeFieldId: 'activeFieldId',
  hasEnvelope: 'hasEnvelope'
})

const { getUser, getEnvelope, getWidget, getPreview, getBulkSendPreview } = useActions({
  getUser: 'auth/getUser',
  getEnvelope: 'getEnvelope',
  getWidget: 'getWidget',
  getPreview: 'getPreview',
  getBulkSendPreview: 'getBulkSendPreview'
})

// eslint-disable-next-line no-unused-vars
const [_, ppPhase2Enabled] = useFlagEnabled('password-protection-phase2-sp')

const loadingData = ref(false)

const loading = computed(() => loadingData.value || (!hasEnvelope.value && !passwordNeeded.value && !otpNeeded.value))

const isPreview = computed(() => route.name === 'Preview' || route.name === 'BulkSendPreview')

async function handleAuthorisation (password) {
  if (ppPhase2Enabled.value) {
    await getUser(password)
    if (!passwordNeeded.value && !otpNeeded.value) {
      fetchEnvelope()
    }
  } else {
    fetchEnvelope(password)
  }
}

function fetchEnvelope (password) {
  loadingData.value = true

  const widgetRoutes = ['SignWidget', 'WidgetComplete', 'WidgetSigned', 'WidgetVerify']
  if (widgetRoutes.includes(route.name)) {
    return getWidget({
      query: route.query,
      widgetCode: route.params.widgetCode
    })
      .finally(() => { loadingData.value = false })
  }

  if (route.name === 'Preview') {
    return getPreview()
      .finally(() => { loadingData.value = false })
  }

  if (route.name === 'BulkSendPreview') {
    return getBulkSendPreview({ query: route.query })
      .finally(() => { loadingData.value = false })
  }

  return getEnvelope(password)
    .finally(() => { loadingData.value = false })
}

onBeforeMount(() => {
  if (!passwordNeeded.value && !otpNeeded.value) {
    fetchEnvelope()
  }

  proxy.$bus.$on('loading', () => {
    loadingData.value = true
  })

  proxy.$bus.$on('loadingStop', () => {
    loadingData.value = false
  })

  proxy.$bus.$on('submitEnvelope', ({ response }) => {
    if (response.status === 'verify') {
      let redirectUrl = widgetRedirectUrl.value

      if (redirectUrl) {
        if (!redirectUrl.match(/^https:\/\/|^http:\/\//g)) {
          redirectUrl = `http://${redirectUrl}`
        }
        redirectUrl = new URL(redirectUrl)

        window.location.replace(redirectUrl)
      } else {
        router.push({
          name: 'WidgetSigned',
          params: {
            widgetCode: route.params.widgetCode
          }
        }).catch(error => {
          console.error(error)
        })
      }
    } else {
      fetchEnvelope()
    }
  })

  proxy.$bus.$on('scrollBody', () => {
    const input = document.querySelector(`.document-field[data-id="${activeFieldId.value}"]`)

    if (input) {
      const rect = input.getBoundingClientRect()
      const scrollAmount = rect.top + window.scrollY

      const header = document.querySelector('header .header')
      const headerHeight = header.offsetHeight + 70

      document.documentElement.scrollTo({ top: scrollAmount - headerHeight, behavior: 'smooth' })
    }
  })
})

watch(documentIndex, () => {
  document.body.scrollTop = 0
  document.documentElement.scrollTop = 0
})
</script>

<script>
export default {
  name: 'EnvelopeView'
}
</script>
